import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { useParams } from "react-router-dom";
import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import { exportDocumentCsv, fetchDocumentReport } from "../../../../services/reports";
import { UITableShell, UITableToolbar } from "../../../../components/ui";
import ReportColumnVisibilityButton from "../shared/ReportColumnVisibilityButton";
import ReportSavedReportsButton from "../shared/ReportSavedReportsButton";
import { parseSavedBoolean, normalizeHiddenColumns as normalizeSavedHiddenColumns } from "../shared/savedReportUtils";
import {
  reportContentSx,
  reportFilterCardSx,
  reportFilterGridSx,
  reportPageWrapperSx,
  reportSecondaryShellSx,
  reportShellSx,
  reportSummaryCardSx,
  reportTableBodySx,
  reportTableContainerSx,
  reportTableHeaderCellSx,
  reportTablePaperSx,
  reportTableSectionSx,
} from "../shared/reportLayout";

const currency = (value) => `CA$${(Number(value) || 0).toFixed(2)}`;

const DEFAULT_SUMMARY = {
  salesOrderCount: 0,
  inProductionCount: 0,
  invoiceCount: 0,
  purchaseOrderCount: 0,
  salesOrderValue: 0,
  inProductionValue: 0,
  invoiceValue: 0,
  purchaseOrderValue: 0,
  inProductionMargin: 0,
  totalRecords: 0,
};

const DEFAULT_FILTERS = {
  dateFrom: "",
  dateTo: "",
  periodPreset: "fiscalYear",
  representativeType: "all",
  representative: "",
  includeInactive: false,
  quickFind: "",
  groupBy: "none",
};

const ordinal = (day) => {
  const mod10 = day % 10;
  const mod100 = day % 100;
  if (mod10 === 1 && mod100 !== 11) return "st";
  if (mod10 === 2 && mod100 !== 12) return "nd";
  if (mod10 === 3 && mod100 !== 13) return "rd";
  return "th";
};

const friendlyDate = (value) => {
  if (!value) return "\u2014";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "\u2014";
  const month = date.toLocaleString("en-US", { month: "long", timeZone: "UTC" });
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  return `${month} ${day}${ordinal(day)}, ${year}`;
};

const normalizeAvatarUrl = (imgSrc) => {
  if (!imgSrc) return null;
  if (Array.isArray(imgSrc)) {
    const selectedPic = imgSrc.find((pic) => pic?.selected) || imgSrc[0];
    return normalizeAvatarUrl(selectedPic?.url || null);
  }
  if (typeof imgSrc === "string") {
    if (/^https?:\/\//i.test(imgSrc)) return imgSrc;
    if (imgSrc.startsWith("/")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}${imgSrc}`;
    if (imgSrc.startsWith("uploads")) return `${process.env.REACT_APP_NODEJS_SERVER_URL}/${imgSrc}`;
    try {
      const parsed = JSON.parse(imgSrc);
      if (Array.isArray(parsed)) return normalizeAvatarUrl(parsed);
    } catch {
      return imgSrc;
    }
  }
  return imgSrc;
};

const repDisplayName = (rep) => {
  const name = [rep?.first_name || rep?.firstName, rep?.last_name || rep?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || rep?.email || (rep?.id != null ? `User ${rep.id}` : "");
};

const RepList = ({ reps, fallback = "Unassigned" }) => {
  const list = Array.isArray(reps) ? reps.filter(Boolean) : [];
  if (!list.length) return <span>{fallback || "Unassigned"}</span>;

  const allNames = list.map(repDisplayName).filter(Boolean).join(", ") || fallback || "Unassigned";
  const initials = (name) =>
    String(name || "")
      .split(/\s+/)
      .map((part) => part[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }} title={allNames}>
      {list.slice(0, 3).map((rep, index) => (
        <Avatar
          key={`${rep?.id || rep?.email || repDisplayName(rep) || "rep"}-${index}`}
          src={normalizeAvatarUrl(rep?.profilePicture || rep?.avatar || rep?.photoUrl) || undefined}
          alt={repDisplayName(rep)}
          sx={{ width: 26, height: 26, fontSize: 12 }}
        >
          {initials(repDisplayName(rep))}
        </Avatar>
      ))}
      <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
        {allNames}
      </Box>
      {list.length > 3 ? <Chip size="small" variant="outlined" label={`+${list.length - 3}`} /> : null}
    </Box>
  );
};

const getColumnValue = (row, field) => (!row || !field ? undefined : field.split(".").reduce((value, key) => value?.[key], row));

const compareValues = (a, b) => {
  const aNum = Number(a);
  const bNum = Number(b);
  if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;
  const aDate = new Date(a);
  const bDate = new Date(b);
  if (!Number.isNaN(aDate.getTime()) && !Number.isNaN(bDate.getTime())) return aDate - bDate;
  return String(a || "").localeCompare(String(b || ""));
};

const getColumnKey = (column = {}) => String(column.field || column.headerName || "").trim();

const ReportTable = ({
  rows = [],
  columns = [],
  emptyMessage = "No data available.",
  sortField = "salesOrderDate",
  sortDirection = "desc",
  onSortChange = () => {},
}) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const safeColumns = Array.isArray(columns) ? columns : [];

  const sortedRows = useMemo(() => {
    const nextRows = [...safeRows];
    if (!sortField) return nextRows;
    nextRows.sort((left, right) => {
      const result = compareValues(getColumnValue(left, sortField), getColumnValue(right, sortField));
      return sortDirection === "desc" ? -result : result;
    });
    return nextRows;
  }, [safeRows, sortDirection, sortField]);

  if (!safeRows.length) {
    return (
      <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
        <Typography>{emptyMessage}</Typography>
      </Paper>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        ...reportTableContainerSx,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "visible",
      }}
    >
      <Table size="small" stickyHeader sx={{ minWidth: 1560 }}>
        <TableHead>
          <TableRow>
            {safeColumns.map((column) => {
              const active = sortField === column.field;
              const nextDirection = active && sortDirection === "asc" ? "desc" : "asc";
              return (
                <TableCell
                  key={getColumnKey(column)}
                  onClick={() => onSortChange({ field: column.field, direction: nextDirection })}
                  sx={reportTableHeaderCellSx({
                    active,
                    sortable: true,
                    minWidth: column.minWidth || 120,
                  })}
                >
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                    <Box component="span">{column.headerName || column.field}</Box>
                    {active ? (
                      sortDirection === "asc"
                        ? <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                        : <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                    ) : null}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody sx={reportTableBodySx}>
          {sortedRows.map((row, rowIndex) => (
            <TableRow key={row?.id || `${rowIndex}`} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
              {safeColumns.map((column) => {
                const value = getColumnValue(row, column.field);
                const renderedValue = typeof column.renderCell === "function"
                  ? column.renderCell({ value, row })
                  : (value ?? "\u2014");
                return (
                  <TableCell key={getColumnKey(column)} sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                    {renderedValue}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const DocumentReport = () => {
  const { instanceUniqueId } = useParams();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [groupedSummary, setGroupedSummary] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [sortField, setSortField] = useState("salesOrderDate");
  const [sortDirection, setSortDirection] = useState("desc");
  const [groupSortField, setGroupSortField] = useState("salesOrderValue");
  const [groupSortDirection, setGroupSortDirection] = useState("desc");
  const reportRequestSeq = useRef(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem("eliteCore:reports:documents:columns");
      const parsed = raw ? JSON.parse(raw) : [];
      setHiddenColumns(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch {
      setHiddenColumns([]);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("eliteCore:reports:documents:columns", JSON.stringify(hiddenColumns));
  }, [hiddenColumns]);

  const load = useCallback(async (overrideFilters = null) => {
    const requestId = ++reportRequestSeq.current;
    setLoading(true);
    try {
      const effectiveFilters = overrideFilters && typeof overrideFilters === "object"
        ? overrideFilters
        : filters;
      const res = await fetchDocumentReport({ instanceUniqueId, ...effectiveFilters });
      if (requestId !== reportRequestSeq.current) return;
      setRows(Array.isArray(res?.rows) ? res.rows : []);
      setSummary(
        res?.summary && typeof res.summary === "object"
          ? { ...DEFAULT_SUMMARY, ...res.summary }
          : DEFAULT_SUMMARY,
      );
      setGroupedSummary(Array.isArray(res?.groupedSummary) ? res.groupedSummary : []);
    } catch (error) {
      if (requestId !== reportRequestSeq.current) return;
      console.error("Failed to load document report", error);
      setRows([]);
      setGroupedSummary([]);
      setSummary(DEFAULT_SUMMARY);
    } finally {
      if (requestId === reportRequestSeq.current) setLoading(false);
    }
  }, [filters, instanceUniqueId]);

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      try {
        const res = await fetchDocumentReport({ instanceUniqueId, ...DEFAULT_FILTERS });
        setRows(Array.isArray(res?.rows) ? res.rows : []);
        setSummary(
          res?.summary && typeof res.summary === "object"
            ? { ...DEFAULT_SUMMARY, ...res.summary }
            : DEFAULT_SUMMARY,
        );
        setGroupedSummary(Array.isArray(res?.groupedSummary) ? res.groupedSummary : []);
      } catch (error) {
        console.error("Failed to load document report", error);
        setRows([]);
        setGroupedSummary([]);
        setSummary(DEFAULT_SUMMARY);
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, [instanceUniqueId]);

  const columns = useMemo(() => ([
    {
      field: "repName",
      headerName: "Rep",
      minWidth: 220,
      renderCell: ({ row }) => <RepList reps={row.repDetails} fallback={row.repName} />,
    },
    { field: "repType", headerName: "Rep Type", minWidth: 140 },
    { field: "customerName", headerName: "Customer", minWidth: 180 },
    { field: "projectTitle", headerName: "Project", minWidth: 180 },
    { field: "orderNumber", headerName: "Order #", minWidth: 110 },
    { field: "salesOrderDate", headerName: "Sales Order Date", minWidth: 160, renderCell: (params) => friendlyDate(params.value) },
    { field: "productionDate", headerName: "Production Date", minWidth: 160, renderCell: (params) => friendlyDate(params.value) },
    { field: "invoiceDate", headerName: "Invoice Date", minWidth: 160, renderCell: (params) => friendlyDate(params.value) },
    { field: "purchaseOrderDate", headerName: "Purchase Order Date", minWidth: 180, renderCell: (params) => friendlyDate(params.value) },
    { field: "salesOrderCount", headerName: "Sales Orders", minWidth: 120 },
    { field: "inProductionCount", headerName: "In Production", minWidth: 130 },
    { field: "invoiceCount", headerName: "Invoices", minWidth: 110 },
    { field: "purchaseOrderCount", headerName: "Purchase Orders", minWidth: 140 },
    { field: "salesOrderValue", headerName: "Sales Order Value", minWidth: 150, renderCell: (params) => currency(params.value) },
    { field: "inProductionValue", headerName: "In-Production Value", minWidth: 160, renderCell: (params) => currency(params.value) },
    { field: "invoiceValue", headerName: "Invoice Value", minWidth: 140, renderCell: (params) => currency(params.value) },
    { field: "purchaseOrderValue", headerName: "Purchase Order Value", minWidth: 170, renderCell: (params) => currency(params.value) },
    { field: "inProductionMargin", headerName: "In-Production Margin", minWidth: 170, renderCell: (params) => currency(params.value) },
  ]), []);

  const groupedColumns = useMemo(() => ([
    { field: "group", headerName: "Group", minWidth: 180 },
    { field: "salesOrderCount", headerName: "Sales Orders", minWidth: 120 },
    { field: "inProductionCount", headerName: "In Production", minWidth: 130 },
    { field: "invoiceCount", headerName: "Invoices", minWidth: 110 },
    { field: "purchaseOrderCount", headerName: "Purchase Orders", minWidth: 140 },
    { field: "salesOrderValue", headerName: "Sales Order Value", minWidth: 150, renderCell: (params) => currency(params.value) },
    { field: "inProductionValue", headerName: "In-Production Value", minWidth: 160, renderCell: (params) => currency(params.value) },
    { field: "invoiceValue", headerName: "Invoice Value", minWidth: 140, renderCell: (params) => currency(params.value) },
    { field: "purchaseOrderValue", headerName: "Purchase Order Value", minWidth: 170, renderCell: (params) => currency(params.value) },
    { field: "inProductionMargin", headerName: "In-Production Margin", minWidth: 170, renderCell: (params) => currency(params.value) },
    { field: "totalRecords", headerName: "Records", minWidth: 100 },
  ]), []);

  useEffect(() => {
    const validColumnKeys = new Set(
      [...columns, ...groupedColumns].map((column) => getColumnKey(column)).filter(Boolean),
    );
    setHiddenColumns((previous) => {
      const next = (Array.isArray(previous) ? previous : []).filter((key) => validColumnKeys.has(String(key)));
      return next.length >= validColumnKeys.size ? [] : next;
    });
  }, [columns, groupedColumns]);

  const visibleColumns = useMemo(() => {
    const hiddenSet = new Set((Array.isArray(hiddenColumns) ? hiddenColumns : []).map(String));
    const nextColumns = columns.filter((column) => !hiddenSet.has(getColumnKey(column)));
    return nextColumns.length ? nextColumns : columns;
  }, [columns, hiddenColumns]);

  const visibleGroupedColumns = useMemo(() => {
    const hiddenSet = new Set((Array.isArray(hiddenColumns) ? hiddenColumns : []).map(String));
    const nextColumns = groupedColumns.filter((column) => !hiddenSet.has(getColumnKey(column)));
    return nextColumns.length ? nextColumns : groupedColumns;
  }, [groupedColumns, hiddenColumns]);

  const currentSavedReportState = useMemo(() => ({
    version: 1,
    filters: {
      dateFrom: filters.dateFrom || "",
      dateTo: filters.dateTo || "",
      periodPreset: filters.periodPreset || "fiscalYear",
      representativeType: filters.representativeType || "all",
      representative: filters.representative || "",
      includeInactive: Boolean(filters.includeInactive),
      quickFind: filters.quickFind || "",
      groupBy: filters.groupBy || "none",
    },
    hiddenColumns: Array.isArray(hiddenColumns) ? hiddenColumns.filter(Boolean) : [],
    sort: {
      field: sortField,
      direction: sortDirection,
    },
    groupedSort: {
      field: groupSortField,
      direction: groupSortDirection,
    },
  }), [filters, groupSortDirection, groupSortField, hiddenColumns, sortDirection, sortField]);

  const applySavedReportState = useCallback(async (savedState = {}) => {
    const savedFilters = savedState?.filters && typeof savedState.filters === "object"
      ? savedState.filters
      : {};
    const validColumnKeys = new Set(
      [...columns, ...groupedColumns].map((column) => getColumnKey(column)).filter(Boolean),
    );

    const nextHiddenColumns = normalizeSavedHiddenColumns({
      savedState,
      validColumnKeys: Array.from(validColumnKeys),
      fallbackHiddenColumns: [],
    });

    const nextSortField = columns.some((column) => column.field === savedState?.sort?.field)
      ? savedState.sort.field
      : "salesOrderDate";
    const nextSortDirection = savedState?.sort?.direction === "asc" ? "asc" : "desc";
    const nextGroupSortField = groupedColumns.some((column) => column.field === savedState?.groupedSort?.field)
      ? savedState.groupedSort.field
      : "salesOrderValue";
    const nextGroupSortDirection = savedState?.groupedSort?.direction === "asc" ? "asc" : "desc";

    const nextFilters = {
      ...DEFAULT_FILTERS,
      ...savedFilters,
      dateFrom: String(savedFilters?.dateFrom || ""),
      dateTo: String(savedFilters?.dateTo || ""),
      periodPreset: String(savedFilters?.periodPreset || "fiscalYear"),
      representativeType: String(savedFilters?.representativeType || "all"),
      representative: String(savedFilters?.representative || ""),
      includeInactive: parseSavedBoolean(savedFilters?.includeInactive, false),
      quickFind: String(savedFilters?.quickFind || ""),
      groupBy: String(savedFilters?.groupBy || "none"),
    };

    setHiddenColumns(nextHiddenColumns);
    setSortField(nextSortField);
    setSortDirection(nextSortDirection);
    setGroupSortField(nextGroupSortField);
    setGroupSortDirection(nextGroupSortDirection);
    setFilters(nextFilters);
    await load(nextFilters);
  }, [columns, groupedColumns, load]);

  const summaryCards = [
    ["Sales Orders", summary.salesOrderCount ?? 0],
    ["In Production", summary.inProductionCount ?? 0],
    ["Invoices", summary.invoiceCount ?? 0],
    ["Purchase Orders", summary.purchaseOrderCount ?? 0],
    ["Sales Order Value", currency(summary.salesOrderValue)],
    ["In-Production Value", currency(summary.inProductionValue)],
    ["Invoice Value", currency(summary.invoiceValue)],
    ["In-Production Margin", currency(summary.inProductionMargin)],
    ["Total Records", summary.totalRecords ?? 0],
  ];

  const onExport = async () => {
    const response = await exportDocumentCsv({ instanceUniqueId, ...filters });
    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: "text/csv;charset=utf-8;" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "document-report.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <div className="instance-dashboard">
      <InstanceLeftSidebar />
      <div className="rightSide">
        <Box className="fade-in-effect-usability" sx={reportPageWrapperSx}>
          <UITableShell customLayout paperSx={reportShellSx}>
            <UITableToolbar sx={{ backgroundColor: "background.paper", flexShrink: 0 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1.5}
                sx={{ width: "100%" }}
              >
                <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" useFlexGap>
                  <Typography variant="h6" fontWeight={700} sx={{ color: "text.primary", lineHeight: 1 }}>
                    Documents Report
                  </Typography>
                  <Chip
                    label={loading ? "Loading..." : `${Array.isArray(rows) ? rows.length : 0} total`}
                    size="small"
                    sx={{
                      backgroundColor: "var(--ec-status-neutral-bg)",
                      color: "var(--ec-status-neutral-text)",
                      border: "1px solid var(--ec-status-neutral-border)",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 22,
                    }}
                  />
                  <Chip
                    label="documents"
                    size="small"
                    sx={{
                      backgroundColor: "var(--ec-status-info-bg)",
                      color: "var(--ec-status-info-text)",
                      border: "1px solid var(--ec-status-info-border)",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      height: 22,
                      textTransform: "none",
                    }}
                  />
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <ReportSavedReportsButton
                    instanceUniqueId={instanceUniqueId}
                    reportKey="documents"
                    currentState={currentSavedReportState}
                    onApplyReport={applySavedReportState}
                  />
                  <ReportColumnVisibilityButton
                    columns={columns}
                    hiddenColumns={hiddenColumns}
                    onHiddenColumnsChange={setHiddenColumns}
                  />
                  <Tooltip title="Row density">
                    <span>
                      <IconButton size="small" disabled sx={{ color: "text.secondary" }}>
                        <SettingsIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 24, alignSelf: "center" }} />
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disableElevation
                    startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                    onClick={onExport}
                    sx={{
                      fontWeight: 700,
                      textTransform: "none",
                      height: 34,
                      px: 2,
                      boxShadow: "none",
                      "&:hover": { boxShadow: "none" },
                    }}
                  >
                    Export CSV
                  </Button>
                </Stack>
              </Stack>
            </UITableToolbar>

            <Box sx={{ ...reportContentSx, minWidth: 0, maxWidth: "100%" }}>
              <Paper elevation={0} sx={reportFilterCardSx}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                    mb: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Documents Report Controls
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Review document activity, booked revenue, production movement, and margins by representative.
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button variant="outlined" size="small" onClick={load}>
                      Get Report
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => setFilters(DEFAULT_FILTERS)}>
                      Reset
                    </Button>
                  </Stack>
                </Box>

                <Box sx={reportFilterGridSx}>
                  <TextField
                    label="Date From"
                    type="date"
                    value={filters.dateFrom}
                    onChange={(event) => setFilters((previous) => ({
                      ...previous,
                      dateFrom: event.target.value,
                      periodPreset: "custom",
                    }))}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    label="Date To"
                    type="date"
                    value={filters.dateTo}
                    onChange={(event) => setFilters((previous) => ({
                      ...previous,
                      dateTo: event.target.value,
                      periodPreset: "custom",
                    }))}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    select
                    label="Period Preset"
                    value={filters.periodPreset}
                    onChange={(event) => setFilters((previous) => ({
                      ...previous,
                      periodPreset: event.target.value,
                    }))}
                    SelectProps={{ native: true }}
                    size="small"
                  >
                    <option value="fiscalYear">Fiscal Year</option>
                    <option value="calendarYear">Calendar Year</option>
                    <option value="custom">Custom Range</option>
                  </TextField>
                  <TextField
                    select
                    label="Representative Type"
                    value={filters.representativeType}
                    onChange={(event) => setFilters((previous) => ({
                      ...previous,
                      representativeType: event.target.value,
                    }))}
                    SelectProps={{ native: true }}
                    size="small"
                  >
                    <option value="all">All reps</option>
                    <option value="clientRep">Client Rep</option>
                    <option value="orderRep">Order Rep</option>
                  </TextField>
                  <TextField
                    label="Representative"
                    value={filters.representative}
                    onChange={(event) => setFilters((previous) => ({
                      ...previous,
                      representative: event.target.value,
                    }))}
                    size="small"
                    helperText="Filter by rep ID, name, or email"
                  />
                  <TextField
                    label="Quick Find"
                    value={filters.quickFind}
                    onChange={(event) => setFilters((previous) => ({
                      ...previous,
                      quickFind: event.target.value,
                    }))}
                    size="small"
                    helperText="Order, project, customer, invoice, PO, or rep"
                  />
                  <TextField
                    select
                    label="Group By"
                    value={filters.groupBy}
                    onChange={(event) => setFilters((previous) => ({
                      ...previous,
                      groupBy: event.target.value,
                    }))}
                    SelectProps={{ native: true }}
                    size="small"
                  >
                    <option value="none">None</option>
                    <option value="rep">Rep</option>
                    <option value="month">Month</option>
                    <option value="quarter">Quarter</option>
                  </TextField>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={filters.includeInactive}
                        onChange={(event) => setFilters((previous) => ({
                          ...previous,
                          includeInactive: event.target.checked,
                        }))}
                      />
                    )}
                    label="Include inactive representatives"
                  />
                </Box>
              </Paper>

              <Box
                sx={{
                  display: "grid",
                  gap: 1.5,
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                    xl: "repeat(9, minmax(0, 1fr))",
                  },
                }}
              >
                {summaryCards.map(([label, value]) => (
                  <Paper key={label} elevation={0} sx={reportSummaryCardSx}>
                    <Typography variant="caption" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {value}
                    </Typography>
                  </Paper>
                ))}
              </Box>

              <Box sx={{ ...reportTableSectionSx, minWidth: 0, maxWidth: "100%" }}>
                <Paper
                  elevation={0}
                  sx={{ ...reportTablePaperSx, minWidth: 0, maxWidth: "100%", overflow: "hidden" }}
                >
                  <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden" }}>
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: "100%",
                        minWidth: 0,
                        overflowX: "auto",
                        overflowY: "hidden",
                      }}
                    >
                      {loading ? (
                        <Box sx={{ p: 4 }}>
                          <Typography>Loading document report...</Typography>
                        </Box>
                      ) : (
                        <ReportTable
                          rows={rows}
                          columns={visibleColumns}
                          emptyMessage="No document activity found for selected filters."
                          sortField={sortField}
                          sortDirection={sortDirection}
                          onSortChange={({ field, direction }) => {
                            setSortField(field);
                            setSortDirection(direction);
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Paper>
              </Box>

              {!loading && groupedSummary.length > 0 ? (
                <UITableShell paperSx={reportSecondaryShellSx}>
                  <UITableToolbar sx={{ backgroundColor: "background.paper" }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Grouped Summary
                    </Typography>
                  </UITableToolbar>
                  <Box sx={{ p: { xs: 1, md: 1.5 }, minWidth: 0, maxWidth: "100%" }}>
                    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflow: "hidden" }}>
                      <Box
                        sx={{
                          width: "100%",
                          maxWidth: "100%",
                          minWidth: 0,
                          overflowX: "auto",
                          overflowY: "hidden",
                        }}
                      >
                        <ReportTable
                          rows={groupedSummary}
                          columns={visibleGroupedColumns}
                          emptyMessage="No grouped summary data."
                          sortField={groupSortField}
                          sortDirection={groupSortDirection}
                          onSortChange={({ field, direction }) => {
                            setGroupSortField(field);
                            setGroupSortDirection(direction);
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </UITableShell>
              ) : null}
            </Box>
          </UITableShell>
        </Box>
      </div>
    </div>
  );
};

export default DocumentReport;
